import { createContext, useCallback, use, useMemo, useState } from "react"

type ErrorContextProps = {
  errors: {
    error: string | null
    id: string | null
  }[]
  addError: (error: string, id: string) => void
  removeError: (id: string) => void
}

const TableErrorContext = createContext<ErrorContextProps>({
  errors: [],
  addError: () => {},
  removeError: () => {},
})

export const TableErrorProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [errors, setErrors] = useState<
    {
      error: string | null
      id: string | null
    }[]
  >([])

  const addError = useCallback((error: string, id: string) => {
    setErrors((prev) => [...prev, { error, id }])
  }, [])

  const removeError = useCallback((id: string) => {
    setErrors((prev) => prev.filter((error) => error.id !== id))
  }, [])

  const value = useMemo(
    () => ({ errors, addError, removeError }),
    [errors, addError, removeError]
  )
  return <TableErrorContext value={value}>{children}</TableErrorContext>
}

export const TableErrorMessage = () => {
  const { errors } = useTableErrorContext()
  return (
    <footer className="text-red-400 text-sm flex flex-col gap-2">
      {errors.map((error) => (
        <p key={error.id}>{error.error}</p>
      ))}
    </footer>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTableErrorContext = () => {
  const context = use(TableErrorContext)
  if (!context) {
    throw new Error(
      `useTableErrorContext must be used within a TableErrorProvider`
    )
  }
  return context
}
